import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Checks freshness of /index.html on every React Router navigation using a HEAD request.
 * If the ETag (or Last-Modified) changes between navigations, reloads the page to fetch the new build.
 * Requests are throttled to one per minute per tab and duplicate router keys are ignored to
 * avoid unnecessary network traffic while preserving freshness guarantees.
 *
 * Requirements on hosting:
 * - Serve index.html with: Cache-Control: no-cache (so the browser revalidates on every request)
 * - Ensure GCP returns either ETag or Last-Modified for index.html (GCS/Cloud Run do by default)
 */
const NO_KEY_FALLBACK = '__useIndexHtmlChecker__no-key__';
const POLL_INTERVAL_MS = 60_000; // 1 minute
const INITIAL_NAV_IGNORE_MS = 1_000;
const IS_DEV = import.meta.env.DEV;

export function useIndexHtmlChecker() {
  const location = useLocation();
  const lastTagRef = useRef<string | null>(null);
  const lastCheckTimeRef = useRef<number | null>(null);
  const lastLocationKeyRef = useRef<string | null>(null);
  const mountTimeRef = useRef(window.performance.now());

  useEffect(() => {
    if (IS_DEV) {
      return;
    }

    const locationKey = location.key ?? NO_KEY_FALLBACK;

    if (locationKey === lastLocationKeyRef.current) {
      return;
    }

    lastLocationKeyRef.current = locationKey;

    const now = window.performance.now();

    if (lastCheckTimeRef.current !== null && now - lastCheckTimeRef.current < POLL_INTERVAL_MS) {
      return;
    }

    const abort = new AbortController();
    const checkIndexHtml = async () => {
      try {
        const res = await window.fetch('/index.html', {
          method: 'HEAD',
          cache: 'no-cache', // force conditional revalidation with ETag/Last-Modified
          signal: abort.signal,
        });

        const currentTag = res.headers.get('ETag') || res.headers.get('Last-Modified');
        const executedAt = window.performance.now();

        if (executedAt - mountTimeRef.current < INITIAL_NAV_IGNORE_MS) {
          if (lastTagRef.current === null) {
            lastTagRef.current = currentTag;
          }
          lastCheckTimeRef.current = executedAt; // ensure throttling
          return;
        }

        if (!lastTagRef.current) {
          // First navigation after mount: store the tag and do nothing
          lastTagRef.current = currentTag;
          lastCheckTimeRef.current = executedAt;
          return;
        }

        if (currentTag && currentTag !== lastTagRef.current) {
          // New build detected
          // Clear the ref to avoid loops in edge cases, then reload
          lastTagRef.current = currentTag;
          lastCheckTimeRef.current = executedAt;
          // Full page reload to fetch the latest index.html and asset hashes
          window.location.reload();
          return;
        }

        lastTagRef.current = currentTag;
        lastCheckTimeRef.current = executedAt;
      } catch (err) {
        // Non-fatal: if the check fails, just continue without reloading
        if (import.meta.env.DEV) {
          console.warn('[useIndexHtmlChecker] Failed to HEAD /index.html', err);
        }
      }
    };

    checkIndexHtml();
    return () => abort.abort();
  }, [location]);
}
